/* eslint-disable */

import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAuth } from '../util/firebaseUtil';
import { DataPoint, PreviousResponsesInterface, ProcedureStatusInterface, RecordAlerts } from '../types';
import { CarryForwardType, isAnyProcedureMode, isSyntheticQuestionType, ProcedureDisplayType, ProcedureOverrideStatus, ProcedureStatus, QuestionFixedAnswerType, SyntheticQuestionType } from '../enums';
import { RulesAlertDialog } from '../components/Procedure/Dialog';
import { fetchPreviousDataPoints, saveAutoQueries, saveDataPoints } from '../api/esourceService';
import { ProcedureRecord, ProcedureRecords, SimplifiedRecords, UnresolvedAutoQuery } from './types';
import {
  applyChangeReason,
  applyDefaultRecordIfNecessary,
  applyDeletion,
  applyMiscellaneous,
  applyAnswerComments,
  applyNotDone,
  applyProcedureStatus,
  applyVisitStatus,
  deepCopyRecords,
  findSyntheticDataPointFromRecords,
  getAllChangedAnswers,
  getAllUnansweredQuestions,
  getDataPointsForSaving,
  getHasUnsavedChanges,
  getInitialRecords,
  getPreviousResponsesFromDataPoints,
  getUpdatedRecords,
  pruneRecordsIfNecessary,
  wasAnythingEverAnswered,
} from './util';
import DeleteRecordDialog from '../components/Procedure/Dialog/DeleteRecordDialog';
import SaveDataPointErrorDialog from '../components/Procedure/Dialog/SaveDataPointErrorDialog';
import BlurOverlay from '../components/BlurOverlay';
import {
    applyProcedureAccessHash,
    applyToggleMap,
    computeToggleAllUpdate,
    enrichAutoQueries
} from './util/recordMutationUtils';
import { createDefaultDataPoint, DataPointCreationContext, filterSyntheticRecordsForMultiRecordProcedures, getDataPointsFromProcedureRecord, getDataPointsFromProcedureRecords, getReferencedRecords, justAnswersFromProcedureRecord, resolveDataPointForRecord, shouldRunRulesInitiallyForDataPoints } from './util/recordUtils';
import { getAncillaryDataPoint } from '../util/dataPointUtil';
import { useProcedureRules } from '../hooks/useProcedureRules';
import { AnswerChangeOptions, DeleteRecordDialogInterface, ProcedureContextProviderProps, ProcedureValues, SaveProcedureProps, SaveRequiresTranscriptionProps } from './types/ProcedureContext.types';
import VariableNameAnswers from './types/VariableNameAnswers';
import {
  parseAndExtractVariableNamesFromCondition,
  prependVar,
  prependVarToAnswersForRules,
  turnOnRulesForQuestion
} from '../util/logic/ProcedureLogicUtil';
import { calculateValue, isAnswerPopulated } from '../util/logic/CalculationFunctionUtil';
import { EvaluateRulesArgs, HandleToggleAllAnswersFn, RuleEffects } from '../hooks/useProcedureRules.types';
import { clearAutoQueriesFromList } from '../util/procedureRulesUtils';
import { syncAlertListByRecord } from '../util/alertListItemUtil';
import { populateReferencingRecords } from '../util/referenceQuestionUtil';

const ProcedureContext = createContext<ProcedureValues | Record<string, never>>({});
export default ProcedureContext;

export function ProcedureContextProvider(props: ProcedureContextProviderProps) {
  const { studyId, visitId } = useParams();
  const {
    children,
    nextProcedureId,
    procedureId,
    readOnly = false,
    setShowCompleteVisitPage,
    setProcedureId,
    setProcedureStatuses,
    initialProcedureStatus = ProcedureStatus.NOT_STARTED,
    setHasUnsavedChanges = () => { },
    subjectData,
    setSaveProcedureAlertsDialogValues = () => { },
    visitConfig,
    visitMode,
    visitType,
    dataPoints,
    referenceDataPoints,
    isLoading,
    isRunningInitialRules,
    setIsRunningInitialRules,
    releaseLock: releaseLockProp = () => {},
  } = props;
  const { subjectId } = subjectData;
  const currentProcedure = visitConfig.procedures.find(({ procedureId: pid }) => pid === procedureId)!;
  const {
    carryForwardType,
    displayType,
    name: procedureName,
    needsSecondaryEntry,
    noEntryText,
    order: procedureOrder,
    questions,
    rules,
    templateLevel: procedureTemplateLevel,
    versionId,
    versionName,
  } = currentProcedure;

  const autoQueries = useRef<Array<UnresolvedAutoQuery>>([]);
  const [alerts, setAlerts] = useState<Array<string>>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isNewProcedure, setIsNewProcedure] = useState<boolean>(true);
  const [records, setRecords] = useState<ProcedureRecords>({});
  const [referencedRecords, setReferencedRecords] = useState<ProcedureRecords>({});
  const [prevResponses, setPrevResponses] = useState<Array<PreviousResponsesInterface> | undefined>();
  const [initialRecords, setInitialRecords] = useState<ProcedureRecords>({});
  const [deleteRecordDialogValues, setDeleteRecordDialogValues] = useState<DeleteRecordDialogInterface>({ isOpen: false });
  const [saveDataPointErrors, setSaveDataPointErrors] = useState<Array<string>>([]);
  const [requiresTranscription, setRequiresTranscription] = useState<boolean>(needsSecondaryEntry || false);
  const [notificationAlerts, setNotificationAlerts] = useState<RecordAlerts[]>([]);
  const [isHashMismatchError, setIsHashMismatchError] = useState<boolean>(false);
  const [procedureAccessHash, setProcedureAccessHash] = useState<string | undefined>(undefined);

  /**
   * Generates a ProcedureRecord containing default DataPoint entries for all applicable
   * questions based on the procedure configuration and the specific record ID.
   * Uses helper functions to create default datapoints and resolve their inclusion/modification.
   *
   * @param recordId The ID of the specific record being generated. For the main procedure instance,
   * this will equal procedureId. For child records in a MULTI_RECORD setup,
   * this will be a unique ID for that child record.
   * @returns A ProcedureRecord object ({ [questionId: string]: DataPoint }) containing the
   * defaulted DataPoints for the given recordId.
   */
  const getDefaultedRecord = (recordId: string, isNewRecord?: boolean): ProcedureRecord => {
    const creationContext: DataPointCreationContext = {
      subjectData: {
        patientId: subjectData.patientId,
        siteId: subjectData.siteId,
        subjectStatus: subjectData.subjectStatus,
        subjectId: subjectId ?? subjectData.subjectId ?? '',
      },
      procedureId,
      procedureName,
      procedureOrder,
      procedureTemplateLevel,
      versionId,
      versionName,
      studyId: studyId!,
      visitId: visitId ?? null,
      requiresTranscription,
      displayType,
      carryForwardType: carryForwardType ?? null,
      isNewRecord,
    } as DataPointCreationContext;

    return questions.reduce((record, question) => {
      const defaultPoint = createDefaultDataPoint(question, recordId, creationContext);
      const resolved = resolveDataPointForRecord(
        question,
        defaultPoint,
        recordId,
        procedureId,
        displayType,
      );

      if (resolved) Object.assign(record, resolved);
      return record;
    }, {} as ProcedureRecord);
  };


  const rulesHook = useProcedureRules({ rules, procedureId });
  const {
    rulesProcessing, getRulesEngine, evaluateRulesAndGetEffects, getNotificationsEngine,
  } = rulesHook;

  /**
   * Function to update record values for data point saves.
   * @param update
   */
  const overwriteRecordValues = (update: { procedure_override_status?: ProcedureOverrideStatus, needs_secondary_entry?: boolean }) => {
    setRecords((currentRecords) => {
      const newRecords: ProcedureRecords = { ...currentRecords };
      Object.keys(newRecords).forEach((recordId) => {
        const { record } = newRecords[recordId];
        Object.keys(record).forEach((questionId) => {
          record[questionId] = { ...record[questionId], ...update };
        });
      });
      return newRecords;
    });
  };

  const clearAutoQueries = (variableName: string, recordId?: string) => {
    // Update the ref using the result from the utility function
    autoQueries.current = clearAutoQueriesFromList({
      currentList: autoQueries.current,
      variableNameToClear: variableName,
      recordId: recordId || procedureId, // Use specific ID or fallback
    });
  };

  const setICFHardcopyOverride = (toggle: boolean) => overwriteRecordValues({ procedure_override_status: toggle ? ProcedureOverrideStatus.ICF_HARDCOPY : undefined });

  const overwriteNeedsSecondryEntry = (secondaryEntryValue: boolean) => overwriteRecordValues({ needs_secondary_entry: secondaryEntryValue });

  /**
   * Function to updated needs_secondary_entry because of
   * adhoc change to requires transcription flag for procedure during visit
   * @param isChecked
   * @param changeReason
   * @param changeReasonDetails
   */
  const saveRequiresTranscriptionChange = async ({ isChecked, changeReason, changeReasonDetails }: SaveRequiresTranscriptionProps) => {
    const requiresTranscriptionDataPoint: DataPoint = getAncillaryDataPoint({
      studyId: studyId!,
      procedureId,
      currentUser: getAuth().currentUser,
      subjectData,
      visitConfig,
      carryForwardType: currentProcedure.carryForwardType,
      procedureAccessHash,
    });
    requiresTranscriptionDataPoint.needs_secondary_entry = isChecked;
    requiresTranscriptionDataPoint.change_reason = changeReason;
    requiresTranscriptionDataPoint.change_reason_details = changeReasonDetails;

    await saveDataPoints({
      dataPointsToSave: [requiresTranscriptionDataPoint],
      studyId: studyId!,
      visitId: visitConfig.visitId,
      subjectId: subjectData.subjectId,
      procedureId,
    });

    setRequiresTranscription(isChecked);
    overwriteNeedsSecondryEntry(isChecked);
  };

  const getICFHardcopyOverride = (): boolean => getDataPointsFromProcedureRecords(records)
    .some(({ procedure_override_status }) => (procedure_override_status === ProcedureOverrideStatus.ICF_HARDCOPY));


  const addRecord = (recordId: string, isAutoGenerated?: boolean) => {
    const record = getDefaultedRecord(recordId, true);
    const rulesEngine = getRulesEngine(recordId);
    const notificationsEngine = getNotificationsEngine(recordId);

    setRecords((currentRecords) => {
      const newRecords: ProcedureRecords = { ...currentRecords };
      if (!newRecords[recordId]) {
        newRecords[recordId] = { rulesEngine, notificationsEngine, record };
        return newRecords;
      }

      return newRecords;
    });

    if (isAutoGenerated) {
      // If it's an auto-generated multirecord record (i.e. there's no no_entry question, so we
      // automatically add a record for convenience), add it to the initial records as well
      // so we don't detect this as a user initiated change to the procedure answers
      setInitialRecords((currentRecords) => {
        const newRecords: ProcedureRecords = { ...currentRecords };
        if (!newRecords[recordId]) {
          newRecords[recordId] = { rulesEngine: {}, record: { ...record }, notificationsEngine: {} };
          return newRecords;
        }

        return newRecords;
      });
    }
  };

  /**
   * Helper method that clears out and re-enables all questions of an entire procedure (Normal Procedure, Grid)
   * or a given record in a MultiRecord procedure.
   * @param newRecords
   * @param recordId either the multiRecord Id or procedureId of which to clear the questions.
   */
  const clearQuestions = (newRecords: ProcedureRecords, recordId: string) => {
    questions
      // Don't clear out the synthetic questions because they don't exist on the records themselves
      .filter(({ type }) => !isSyntheticQuestionType(type))
      .forEach(({ questionId }) => {
        const dataPoint = newRecords[recordId].record[questionId];
        // re-enable all questions that were disabled via rules
        dataPoint.is_disabled = false;
        // We don't clear answers that are marked [NOT DONE]
        if (dataPoint.answer !== QuestionFixedAnswerType.NOT_DONE) {
          dataPoint.answer = undefined;
          dataPoint.answer_comment = undefined;
          dataPoint.answer_code = undefined;
          // eslint-disable-next-line no-param-reassign
          newRecords[recordId].record[questionId] = { ...dataPoint };
        }
      });
    return newRecords;
  };

  /**
   * Clears questions of the given record in a multiRecord procedure.
   * @param recordId to clear.
   */
  const clearRecord = (recordId: string) => {
    setRecords((currentRecords) => {
      if (currentRecords[recordId]) {
        const newRecords = { ...currentRecords };
        return clearQuestions(newRecords, recordId);
      }
      return currentRecords;
    });
  };

  /**
   * Recursively calls on updateQuestions with calculated value for all calculation questions associated with given question
   * @param targetVariableName
   * @param id
   * @param rulesEngine
   * @param record
   * @param runRules
   * @returns
   */
  const evaluateCalculations = (
    targetVariable: string,
    id: string,
    rulesEngine: any,
    record: ProcedureRecord,
    runRules: boolean,
  ): ProcedureRecord => {
    let activeRecords = { ...record };

    questions
      .filter((question) => question.calculation)
      .forEach(({ calculation, questionId, variableName }) => {

        // Determine whether the calculation question uses the targetVariable
        // and only trigger an update if it does
        const pattern = /^(\w+)\((.*)\)/; // parse format "Function(params)"
        const matches = calculation?.calculationRule.match(pattern) || [];
        const functionName = matches[1] || "";
        const params = matches[2] || "";
        let justAnswers: VariableNameAnswers = justAnswersFromProcedureRecord(activeRecords);
        let targetVariableName;
        let includedVars = [];
        if (functionName === 'Formula') {
          includedVars = parseAndExtractVariableNamesFromCondition(params);
          // since Formula type-questions use the same method to extract variable names as we do for procedure logic,
          // we need to also prepend "var_" to the variable names
          targetVariableName = prependVar(targetVariable);
          justAnswers = prependVarToAnswersForRules(justAnswers);
        } else {
          targetVariableName = targetVariable;
          includedVars = params.split(/\s*,\s*/g).map(p => p.toLowerCase());
        }

        // this is our base case (this will break if any calculations are cyclical)
        if (!calculation || !includedVars.includes(targetVariableName.toLowerCase()) || !record[questionId]) return;

        // Rule evaluations are performed on the following data structure {variableName: answer}
        const calculatedValue = calculateValue(calculation, justAnswers);

        // The calculated value is basically "unanswered", and the current answer
        // is [NOT DONE], so don't touch the target of this calculation.
        if (!isAnswerPopulated(calculatedValue) && activeRecords[questionId].answer === QuestionFixedAnswerType.NOT_DONE) {
          return;
        }

        // If the calculation result is NaN (due to any input being empty), treat it as unanswered
        if (typeof calculatedValue === 'number' && Number.isNaN(calculatedValue)) {
          activeRecords[questionId].answer = undefined;
        // Infinity is a valid JS number but is a String anywhere else. If the value is Infinity, convert it into a String
        } else if (calculatedValue === Infinity) {
          activeRecords[questionId].answer = `${calculatedValue}`;
        // Preserve the trailing zeros by converting to a string with the configured number of decimal places
        } else if (typeof calculatedValue === 'number' && calculation?.numberOfDecimalPlaces) {
          activeRecords[questionId].answer = calculatedValue.toFixed(calculation.numberOfDecimalPlaces);
        } else {
          activeRecords[questionId].answer = calculatedValue;
        }
        Object.keys(activeRecords).filter((recordQuestionId) => activeRecords[recordQuestionId].is_disabled === false); // filter out disabled        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        activeRecords = updateQuestions({
          variableName,
          questionId,
          newDataPoint: activeRecords[questionId],
          id,
          rulesEngine,
          record: activeRecords,
          runRules,
        });
      });
    return activeRecords;
  };

  interface UpdateQuestionArgs {
    variableName?: string,
    questionId: string,
    newDataPoint: DataPoint,
    id: string,
    rulesEngine: any,
    record: { [questionId: string]: DataPoint },
    runRules: boolean,
  }

  /**
   * Updates record state, turns on rules for the given question, and returns the updated
   * active questions to pass to the rules engine
   * @returns
   */
  const updateQuestions = ({
    variableName,
    questionId,
    newDataPoint,
    id,
    rulesEngine,
    record,
    runRules = true,
  }: UpdateQuestionArgs): ProcedureRecord => {
    // need to make a copy of the record to just pass in the new value (not all values) to rules engine for multi select questions
    const activeQuestions = { ...record };
    activeQuestions[questionId] = newDataPoint;

    setRecords((currentRecords: ProcedureRecords) => {
      const newRecords = { ...currentRecords };
      newRecords[id].record[questionId] = { ...newDataPoint };
      return newRecords;
    });

    if (!variableName) {
      return {};
    }

    if (runRules) {
      // Ensure that the rules are turned on for this question and potentially any others that may be updating also like calculations
      turnOnRulesForQuestion(rulesEngine, variableName);
    }

    // Clear out the autoqueries for the calculation
    clearAutoQueries(variableName, newDataPoint.record_id || newDataPoint.procedure_id);

    return evaluateCalculations(variableName, id, rulesEngine, activeQuestions, runRules);
  };
  const applyAccumulatedEffects = (accumulatedEffects: RuleEffects | null, id?: string, isInitialLoad: boolean = false) => {
    if (!accumulatedEffects) { return; }
    // Apply enables/disables (Context calls setRecords with toggleQuestions utility)
    const {
      variablesToEnable = {}, variablesToDisable = {}, alerts: theAlerts = [], autoQueries: theAutoQueries = [], notificationAlerts: theNotificationAlerts = [],
    } = accumulatedEffects;
    setRecords((currentRecords) => {
      // Start from a shallow copy of the full map
      let next = { ...currentRecords };

      // 1) Enable all the requested variables
      next = applyToggleMap(next, questions, variablesToEnable, true);

      // 2) Then disable all the requested variables
      next = applyToggleMap(next, questions, variablesToDisable, false);

      return next;
    });

    if (theAlerts.length > 0) {
      setAlerts((prevAlerts) => {
        const newAlerts = [...prevAlerts];
        theAlerts.forEach((a) => { newAlerts.push(a); });
        return newAlerts;
      });
    }

    if (theAutoQueries.length > 0) {
      // Assuming autoQueriesRef exists and is managed by context
      autoQueries.current = [...autoQueries.current, ...theAutoQueries];
    }

    if (displayType === ProcedureDisplayType.MULTI_RECORD && !isInitialLoad) {
      setNotificationAlerts((prevAlerts) => syncAlertListByRecord(prevAlerts, theNotificationAlerts, id))
    } else {
      setNotificationAlerts(() => { return [...theNotificationAlerts]; });
    }
  };

  /**
   * Handles a change to a single answer. Orchestrates state update, calculation evaluation,
   * and subsequent rule evaluation based on the final calculated state.
   * This is the primary handler recommended for most single answer updates.
   *
   * @param options - Options describing the answer change, including the new data point and potential selection value. See {@link AnswerChangeOptions}.
   * @returns Promise resolving to void once all processing (state updates, calculations, rule evaluation) is complete.
   * @public
   */
  const handleAnswerChange = ({
    variableName,
    questionId,
    newDataPoint,
    recordId,
    runRules = true,
    selectionValueChange, // The specific value selected, potentially different from final answer
  }: AnswerChangeOptions) => {
    const id = recordId || procedureId;
    const { rulesEngine, notificationsEngine, record } = records[id];

    const finalProcedureRecordState = updateQuestions({
      variableName,
      questionId,
      newDataPoint,
      id,
      rulesEngine,
      record,
      runRules,
    });

    // After state updates and calculations, run rule evaluation if specified
    if (runRules && variableName) {
      const justAnswers: VariableNameAnswers = justAnswersFromProcedureRecord(finalProcedureRecordState);

      const evalArgs: EvaluateRulesArgs = {
        recordAfterCalc: finalProcedureRecordState, // Pass calculated state
        changedVars: new Set<string>([variableName]),
        rulesEngine,
        notificationsEngine,
        selectionAnswers: selectionValueChange !== undefined ? {
          ...justAnswers, // Start with final answers
          [variableName]: selectionValueChange, // Override the trigger var with the selection value
        } : undefined,
        isPrepopulated: false,
      };

      // evaluate rules, then apply effects
      evaluateRulesAndGetEffects(evalArgs).then((effects) => applyAccumulatedEffects(effects, id));
    }
  };

  /**
   * Handles a "toggle all" type operation (e.g., grid select/deselect all).
   * Updates state for multiple questions simultaneously, then evaluates rules for all affected questions.
   *
   * @param newAnswer - The answer value to set or remove across affected questions.
   * @param availableAnswerOptions - List of all possible answer options (used for sorting multi-select).
   * @param toggleOn - `true` if toggling on (adding/setting answer), `false` if toggling off (removing/clearing).
   * @param isMultiSelectQuestion - Optional flag indicating if the affected questions are multi-select.
   * @returns void
   * @public
   */
  const handleToggleAllAnswers: HandleToggleAllAnswersFn = (
    newAnswer,
    availableAnswerOptions,
    toggleOn,
    isMultiSelectQuestion,
  ) => {
    const { record: currentRecordData, rulesEngine, notificationsEngine } = records[procedureId];

    // 1. Compute the next state purely using the utility
    const updatedRecordData = computeToggleAllUpdate({
      currentRecordData,
      questions, // Assumes 'questions' prop contains the relevant questions for toggle
      newAnswer,
      availableAnswerOptions,
      toggleOn,
      isMultiSelectQuestion,
    });

    // 2. Update React State
    setRecords((currentRecs) => ({
      ...currentRecs,
      // Update the specific procedure record immutably
      [procedureId]: { ...currentRecs[procedureId], record: updatedRecordData },
    }));

    const allToggledVarNames = questions.map((q) => q.variableName).filter((n): n is string => !!n);
    const allToggledVarNamesSet = new Set(allToggledVarNames);

    const selectionAnswersMap = Object.assign(
      {},
      ...allToggledVarNames.map((vName) => ({ [vName]: toggleOn ? newAnswer : '' })),
    );

    if (allToggledVarNamesSet.size > 0) {
      // Call the hook function (reference might change each render)
      const rulesArgs: EvaluateRulesArgs = {
        recordAfterCalc: updatedRecordData,
        changedVars: allToggledVarNamesSet,
        rulesEngine,
        notificationsEngine,
        selectionAnswers: selectionAnswersMap,
        isPrepopulated: false,
      };

      evaluateRulesAndGetEffects(rulesArgs).then((effects) => applyAccumulatedEffects(effects, procedureId));
    }
  };

  const findSyntheticDataPoint = (type: SyntheticQuestionType): DataPoint | undefined => findSyntheticDataPointFromRecords(records, type, procedureId);

  /**
   * Saves all the currently new or updated records and resets the context to match the result
   * of the server call. Do NOT assume that modifying the records via "setRecords" just before
   * saving is ok. The setState function doesn't guarantee a state update in the same render cycle
   * as when the save occurs. When you use "records" directly after "setRecords" it could be stale.
   * This is why we snapshot the current records at the beginning of the method; to be able to play
   * with the records just before saving in a safe way.
   * @param param0
   */
  const saveProcedure = async ({
    changeReason,
    changeReasonDetails,
    changedAnswers,
    notDoneQueuedChanges,
    notDoneComment,
    skipMissingQuestionsCheck = false,
    skipChangedAnswersCheck = false,
    skipProcedure = false,
    deletingRecord = false,
    successCallback,
  }: SaveProcedureProps) => {
    if (isAnyProcedureMode(visitMode)) { return; }

    // Snapshot the current "records" state
    const recordsCopy: ProcedureRecords = deepCopyRecords(records);
    const initialRecordsCopy: ProcedureRecords = deepCopyRecords(initialRecords);

    pruneRecordsIfNecessary(recordsCopy, initialRecordsCopy, procedureId, findSyntheticDataPoint(SyntheticQuestionType.NO_ENTRY), findSyntheticDataPoint(SyntheticQuestionType.HAS_CHANGES));

    // Apply [NOT DONE] answers
    applyNotDone(recordsCopy, notDoneQueuedChanges, notDoneComment);

    // Calculate the changed answers list
    const changedAnswersRecordQuestionList = changedAnswers || getAllChangedAnswers({
      procedureId,
      displayType,
      initialRecords: initialRecordsCopy,
      currentRecords: recordsCopy,
      questions,
    });

    // Apply deletion if necessary
    if (deletingRecord) {
      const deletedRecordId = changedAnswersRecordQuestionList[0].recordId;
      applyDeletion(recordsCopy, deletedRecordId);
      setNotificationAlerts((prevAlerts) => syncAlertListByRecord(prevAlerts, [], deletedRecordId));
    }

    // Apply change reasons
    applyChangeReason(recordsCopy, changedAnswersRecordQuestionList, changeReason, changeReasonDetails);

    // Apply answer_comments
    applyAnswerComments(recordsCopy, questions);

    // Calculate the unanswered question list
    const missingRecordQuestionsList = getAllUnansweredQuestions({
      procedureId,
      displayType,
      records: recordsCopy,
      questions,
      carryForwardType,
    });

    applyDefaultRecordIfNecessary(recordsCopy, displayType, carryForwardType, initialProcedureStatus, getDefaultedRecord);

    // Apply current procedure status
    const newProcedureStatus = applyProcedureStatus(recordsCopy, missingRecordQuestionsList, skipProcedure, procedureId, carryForwardType);

    // Apply current visit status
    applyVisitStatus(recordsCopy, newProcedureStatus);

    // Apply the rest of the information onto the records (maybe want to split these out for more clarity?)
    applyMiscellaneous(recordsCopy, subjectData, visitConfig, carryForwardType);

    applyProcedureAccessHash(recordsCopy, procedureAccessHash);

    // If there are no missing answers or changed answers, continue saving
    if ((skipMissingQuestionsCheck || missingRecordQuestionsList.length === 0) && (skipChangedAnswersCheck || changedAnswersRecordQuestionList.length === 0)) {
      // Since the blurred overlay pops up while saving, the only way to get to this state
      // and return early would be if the user tried to double-click/spam save
      if (isSaving) return;
      setIsSaving(true);

      const dataPointsToSave = getDataPointsForSaving({
        procedureId,
        initialRecords: initialRecordsCopy,
        currentRecords: recordsCopy,
        deletingRecord,
        visitMode,
        skipProcedure,
      });

      // Lambda to call to show the a Data Point save
      const showDataPointSaveError = (errorDataPoints: Array<DataPoint>) => setSaveDataPointErrors(errorDataPoints.map((dataPoint) => dataPoint.question_name));

      try {
        // Save autoqueries
        await saveAutoQueries({
          autoQueriesToSave: enrichAutoQueries(autoQueries.current, subjectData, visitConfig, currentProcedure),
          studyId: studyId!,
          subjectId,
        });
        // Save the datapoints and update those currently stored in context with the response
        const saveDataPointsResponse = await saveDataPoints({
          dataPointsToSave,
          studyId: studyId!,
          visitId,
          subjectId,
          procedureId,
        });
        setInitialRecords(getUpdatedRecords(initialRecordsCopy, saveDataPointsResponse, displayType));
        setRecords(getUpdatedRecords(recordsCopy, saveDataPointsResponse, displayType));
        setProcedureStatuses((oldStatuses) => {
          const newStatuses: ProcedureStatusInterface[] = JSON.parse(JSON.stringify(oldStatuses));
          const targetProcedureStatusIndex = newStatuses.findIndex(({ procedureId: statusProcedureId }) => statusProcedureId === procedureId);
          if (targetProcedureStatusIndex >= 0) {
            newStatuses[targetProcedureStatusIndex] = newProcedureStatus;
          } else {
            newStatuses.push(newProcedureStatus);
          }
          return newStatuses;
        });

        // todo: for recordIndex - what if we datapoints that both saved and failed?

        setIsSaving(false);
        if (saveDataPointsResponse.success?.length > 0) {
            // update the procedure access hash only if at least one datapoint saved successfully
            setProcedureAccessHash(saveDataPointsResponse.success[0].procedure_access_hash);
        }
        // If only some Data Points save, show an error message listing those that didn't
        if (saveDataPointsResponse.fail?.length > 0) {
          showDataPointSaveError(saveDataPointsResponse.fail);
          setIsHashMismatchError(saveDataPointsResponse.message === "Access hash validation failed.");
          throw new Error('There was an issue saving the Data Points');
        } else if (successCallback) {
          // Otherwise, if the save was successful, call the success callback
          await successCallback();
        }
      } catch (error) {
        setIsSaving(false);
        // If an error response is returned when saving the Data Points, show an error message
        showDataPointSaveError(dataPointsToSave);

        // eslint-disable-next-line no-console
        console.error(error);
        throw error;
      }
    } else {
      const nothingEverAnswered = !wasAnythingEverAnswered(recordsCopy[procedureId].record, changedAnswersRecordQuestionList, missingRecordQuestionsList);
      // Missing answers, open the missing answers dialog flow
      setSaveProcedureAlertsDialogValues({
        isOpen: true,
        deletingRecord: false,
        missingAnswers: missingRecordQuestionsList || [],
        changedAnswers: changedAnswersRecordQuestionList || [],
        successCallback,
        nothingEverAnswered,
        currentProcedure,
      });
    }
  };

  const deleteRecord = (recordId: string) => {
    if (!records[recordId]) {
      return;
    }

    const hasAuditableChanges = Object.keys(records[recordId].record)
      .some((questionId) => !!records[recordId].record[questionId].answer_id);

    const changedAnswers: SimplifiedRecords = [{
      recordId,
      recordIndex: Object.keys(records).indexOf(recordId),
      questions: questions.filter(({ type }) => !isSyntheticQuestionType(type)),
    }];

    let continueHandler: Function;
    if (hasAuditableChanges) {
      continueHandler = () => setSaveProcedureAlertsDialogValues({
        isOpen: true,
        deletingRecord: true,
        missingAnswers: [],
        changedAnswers,
        currentProcedure,
      });
    } else {
      // This record was never even saved on the server, so just remove it without making a server call
      continueHandler = () => {
        setInitialRecords((currentInitialRecords: ProcedureRecords) => {
          const newRecords = { ...currentInitialRecords };
          delete newRecords[recordId];
          return newRecords;
        });
        setRecords((currentRecords: ProcedureRecords) => {
          const newRecords = { ...currentRecords };
          delete newRecords[recordId];
          return newRecords;
        });
        setNotificationAlerts((prevAlerts) => syncAlertListByRecord(prevAlerts, [], recordId));
      };
    }

    setDeleteRecordDialogValues({
      isOpen: true,
      continueHandler,
    });
  };

  const proceedToNextProcedure = () => {
    const nextProcedure = visitConfig.procedures.find(({ procedureId: pid }) => pid === nextProcedureId);
    if (nextProcedure) {
      setProcedureId(nextProcedure.procedureId);
    } else {
      setProcedureId(null);
      setShowCompleteVisitPage(true);
    }
    window.scrollTo(0, 0);
  };

  const clearProcedure = () => {
    setRecords((currentRecords: ProcedureRecords) => {
      const newRecords = { ...currentRecords };
      return clearQuestions(newRecords, procedureId);
    });
  };
  const retrievePreviousResponses = async () => {
    if (prevResponses) {
      return;
    }
    const prevDataPoints = await fetchPreviousDataPoints({
      visitMode, studyId: studyId!, subjectId, visitId, procedureId,
    });
    setPrevResponses(getPreviousResponsesFromDataPoints(prevDataPoints, questions));
  };

  function getVariableNames(record: ProcedureRecord): string[] {
    return getDataPointsFromProcedureRecord(record)
      .sort((a, b) => a.question_order - b.question_order)
      .map(dp => dp.question_variable_name || '')
      .filter(name => Boolean(name));
  }

  interface EvaluateRulesOptions {
    procedureOrRecordId: string;
    record: ProcedureRecord;
    rulesEngine: any;
    notificationsEngine: any;
    isOnlyNotificationRun?: boolean;
    vars?: string[];
  }

  interface ProcessingResult {
    procedureOrRecordId: string;
    effects: RuleEffects | null;
  }

  async function processInitialNotifications({procedureOrRecordId, record, rulesEngine, notificationsEngine, isOnlyNotificationRun = true, vars = []}: EvaluateRulesOptions): Promise<ProcessingResult> {
    const theVars = vars.length === 0 ? getVariableNames(record) : vars;
    const effects = await evaluateRulesAndGetEffects({
      recordAfterCalc: record,
      changedVars: new Set(theVars),
      rulesEngine: rulesEngine,
      notificationsEngine: notificationsEngine,
      isPrepopulated: true,
      isOnlyNotificationRun: isOnlyNotificationRun,
    });
    // Return the ID and the calculated effects.
    return { procedureOrRecordId, effects };
  }

  async function runCalculationsAndProcessNotifications({procedureOrRecordId, rulesEngine, notificationsEngine, record}: EvaluateRulesOptions): Promise<ProcessingResult> {
    const vars = getVariableNames(record);

    const postCalcRecord = vars.reduce(
      (acc, varName) => evaluateCalculations(varName, procedureOrRecordId, rulesEngine, acc, false),
      record,
    );

    return processInitialNotifications({procedureOrRecordId, rulesEngine, notificationsEngine, record: postCalcRecord, isOnlyNotificationRun: false, vars});
  }

  useEffect(() => {
    if (isLoading) return;

    // Structure the datapoints into records
    const loadDataPoints = async () => {
      const fetchedInitialRecords = getInitialRecords({
        displayType,
        getDefaultedRecord,
        getRulesEngine,
        getNotificationsEngine,
        procedureId,
        unfilteredDataPoints: dataPoints,
      });

      const fetchedReferencedRecords = getReferencedRecords({
        procedure: currentProcedure,
        referencedDataPoints: referenceDataPoints || [],
      });

      setIsNewProcedure(carryForwardType === CarryForwardType.PERMANENT || dataPoints.length === 0);
      setInitialRecords(JSON.parse(JSON.stringify(fetchedInitialRecords)));
      setRecords(fetchedInitialRecords);
      setReferencedRecords(fetchedReferencedRecords);
      setProcedureAccessHash(dataPoints.length > 0 ? dataPoints[0].procedure_access_hash : undefined);
      return {fetchedInitialRecords, fetchedReferencedRecords};
    };

    // Invoke loadDataPoints and run initial rules if necessary (traits, carry forward yes, reference questions)
    const processInitialDataPoints = async () => {
      const { fetchedInitialRecords, fetchedReferencedRecords } = await loadDataPoints();
      populateReferencingRecords(fetchedInitialRecords, fetchedReferencedRecords);


      let processingPromises: Promise<ProcessingResult>[] = [];
      if (shouldRunRulesInitiallyForDataPoints(getDataPointsFromProcedureRecords(fetchedInitialRecords))) {
        processingPromises = Object.entries(fetchedInitialRecords).map(([id, proc]) =>
          runCalculationsAndProcessNotifications({procedureOrRecordId: id, rulesEngine: proc.rulesEngine, notificationsEngine: proc.notificationsEngine, record: proc.record})
        );

      } else {
        // For multi-records, we never want to run rules on the synthetic records (no_entry, has_changes)
        const filteredRecords = filterSyntheticRecordsForMultiRecordProcedures(fetchedInitialRecords, displayType, procedureId);
        processingPromises = Object.entries(filteredRecords).map(([id, proc]) =>
          processInitialNotifications({procedureOrRecordId: id, rulesEngine: proc.rulesEngine, notificationsEngine: proc.notificationsEngine, record: proc.record})
        );
      }

      try {
        // Promise.all will wait for all individual processing promises to resolve.
        // Each promise resolves to { procedureOrRecordId, effects }
        const allResults = await Promise.all(processingPromises);

        // Since we are running all records rules in parallel and hook shares state, each copy of
        // ruleEffects has all the state. Just a quirk of doing this async
        const result: ProcessingResult = allResults[0];
        if (result && result.effects) {
          applyAccumulatedEffects(result.effects, result.procedureOrRecordId, true);
        }

      } catch (error) {
        console.error('Error during parallel processing or applying effects:', error);
      } finally {
        setIsRunningInitialRules(false);
      }
    };

    processInitialDataPoints();
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) {
      // Whenever the records change, check to see if there are unsaved changes
      setHasUnsavedChanges(getHasUnsavedChanges(records, initialRecords));
    }
  }, [records]);

  useEffect(() => {
    setRequiresTranscription(needsSecondaryEntry || false);
  }, [needsSecondaryEntry]);

  const value = useMemo(
    () => ({
      carryForwardType,
      children,
      notificationAlerts: notificationAlerts,
      displayType,
      isLoading,
      isRunningRules: isRunningInitialRules,
      isSaving,
      isNewProcedure,
      noEntryText,
      procedureName,
      prevResponses,
      procedureAccessHash,
      procedureId,
      questions,
      readOnly,
      records,
      referencedRecords,
      rulesProcessing,
      visitMode,
      visitType,
      requiresTranscription,
      addRecord,
      clearProcedure,
      clearRecord,
      deleteRecord,
      findSyntheticDataPoint,
      getICFHardcopyOverride,
      handleAnswerChange,
      handleToggleAllAnswers,
      proceedToNextProcedure,
      retrievePreviousResponses,
      saveProcedure,
      setICFHardcopyOverride,
      releaseLock: releaseLockProp,
      saveRequiresTranscriptionChange,
      setRequiresTranscription,
    }),
    [prevResponses, records, isLoading, isSaving, initialRecords, rulesProcessing, notificationAlerts, isRunningInitialRules, readOnly, procedureAccessHash],
  );

  return (
    <ProcedureContext.Provider value={value}>
      {children}
      {isSaving && <BlurOverlay />}
      <RulesAlertDialog alerts={alerts} setAlerts={setAlerts} />
      <DeleteRecordDialog
        {...deleteRecordDialogValues}
        closeHandler={() => setDeleteRecordDialogValues((prevState) => ({ ...prevState, isOpen: false }))}
      />
      {saveDataPointErrors.length > 0
        && (
          <SaveDataPointErrorDialog
            open
            questionNames={saveDataPointErrors}
            closeHandler={() => {setSaveDataPointErrors([]);} }
            isHashMismatch={isHashMismatchError}
          />
        )}
    </ProcedureContext.Provider>
  );
}

ProcedureContextProvider.defaultProps = {
  initialProcedureStatus: ProcedureStatus.NOT_STARTED,
  setHasUnsavedChanges: () => { },
  setSaveProcedureAlertsDialogValues: () => { },
};
